import React, { useState, useEffect, useCallback, useMemo } from "react";
import { clearLedgerLink } from "actions/ledgerLinks.action";
import {
    clearMattersBalances,
    clearMattersPostings,
    getMattersInfo,
} from "actions/matters.action";
import MatterLedgerGrid from "components/LedgerGrid";
import LedgerPostingGrid from "components/LedgerGrid/LedgerTypeGrid";
import LedgerViewTabBar from "components/LedgerGrid/LedgerViewTabBar";
import MatterBalancesCard from "components/MatterBalances/MatterBalancesCard";
import { useDispatch, useSelector } from "react-redux";
import {
    getMattersPostings,
    getMatterClientPostingCurrencies,
} from "services/postingsService";
import {
    buildMatterLedgerTabItems,
    getMatterLedgerReportName,
    getMatterLedgerReportParams,
    matterLedgerTabId,
} from "pages/MatterLedger/matterLedgerTabs";
import { useCompany } from "services/companyService";
import {
    updateCurrentUserSettings,
    useCurrentUserSettings,
} from "services/userSettingsService";
import UndertakingsList from "components/Undertakings/UndertakingsList";
import ViewPostingDetails from "pages/Postings/PostingDetails";
import { Dialog } from "@progress/kendo-react-dialogs";
import ReportViewer from "pages/Reports/ReportViewerComponent";
import { ReportURL } from "api/axios.config";

const USER_SETTING_SHOW_DDA_KEY = "MatterLedgerShowDDAAccount";
const USER_SETTING_SHOW_REVERSALS_KEY = "MatterLedgerShowReversals";

export default function MatterLedgerCardPage({
    reference,
    selTab,
    refreshClicked,
    setRefreshClicked,
    activeItem,
    fromCaseFile = false,
    matterDetails: matterDetailsProp,
}) {
    const dispatch = useDispatch();
    const [showDDA, setShowDDA] = useState(true);
    const [showReversals, setShowReversals] = useState(false);
    const [list, setLists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTabId, setSelectedTabId] = useState(() =>
        matterLedgerTabId({ type: "ALL", currencyCode: null }),
    );
    const [clientPostingCurrencies, setClientPostingCurrencies] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(false);
    const [openReport, setOpenReport] = useState(false);
    const [transNo, setTransNo] = useState("");
    const [showForm, setShowForm] = useState(false);

    const mattersDetailsFromStore = useSelector(
        (state) => state.mattersList.mattersDetails,
    );
    const mattersListState = useSelector((state) => state.mattersList);
    const currentMatterId =
        mattersListState.matterViewDetails[selTab]?.data.matterId;

    const matterId = fromCaseFile ? reference : currentMatterId ?? reference;
    const matterInfo = fromCaseFile
        ? matterDetailsProp ?? mattersDetailsFromStore?.data
        : mattersDetailsFromStore?.data;

    const { data: userSettings } = useCurrentUserSettings();
    const { data: company } = useCompany();

    const multiCurrencyEnabled = Boolean(
        company?.modules?.MultiCurrency ?? company?.Modules?.MultiCurrency,
    );

    const tabItems = useMemo(
        () =>
            buildMatterLedgerTabItems(
                multiCurrencyEnabled,
                clientPostingCurrencies,
            ),
        [multiCurrencyEnabled, clientPostingCurrencies],
    );

    const activeTab = useMemo(
        () =>
            tabItems.find((t) => matterLedgerTabId(t) === selectedTabId) ??
            tabItems[0],
        [tabItems, selectedTabId],
    );

    const onChangeMatter = (id) => {
        dispatch(clearMattersPostings());

        if (!id) {
            dispatch(clearLedgerLink("Matters", ""));
            setLists([]);
            dispatch(clearMattersBalances("Matter Ledger"));
            return;
        }

        dispatch(getMattersInfo(id));
    };

    const loadPostingsForActiveTab = useCallback(async () => {
        const tab = activeTab;
        if (!matterId || !tab || tab.type === "Undertakings") return;
        setLoading(true);
        try {
            const currencyCode =
                tab.type === "Client" && tab.currencyCode
                    ? tab.currencyCode
                    : undefined;
            const data = await getMattersPostings(
                matterId,
                tab.type,
                showReversals,
                currencyCode,
            );
            setLists(data || []);
        } catch (error) {
            console.error("Failed to fetch matter postings:", error);
            setLists([]);
        } finally {
            setLoading(false);
        }
    }, [matterId, showReversals, activeTab]);

    const apiDataFetcher = useCallback(
        async (id) => {
            if (!id) return;
            if (fromCaseFile) {
                await loadPostingsForActiveTab();
                return;
            }
            try {
                const data = await getMattersPostings(id, "ALL", showReversals);
                setLists(data || []);
            } catch (error) {
                console.error("Failed to fetch matter postings:", error);
                setLists([]);
            }
        },
        [showReversals, fromCaseFile, loadPostingsForActiveTab],
    );

    useEffect(() => {
        if (!fromCaseFile && currentMatterId) {
            onChangeMatter(currentMatterId);
        }
    }, [currentMatterId, fromCaseFile]);

    useEffect(() => {
        if (fromCaseFile && reference) {
            onChangeMatter(reference);
            setSelectedTabId(
                matterLedgerTabId({ type: "ALL", currencyCode: null }),
            );
        }
    }, [reference, fromCaseFile]);

    useEffect(() => {
        const valid = tabItems.some((t) => matterLedgerTabId(t) === selectedTabId);
        if (!valid && tabItems.length) {
            setSelectedTabId(matterLedgerTabId(tabItems[0]));
        }
    }, [tabItems, selectedTabId]);

    useEffect(() => {
        if (!fromCaseFile || !matterId || !multiCurrencyEnabled) {
            setClientPostingCurrencies([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const rows = await getMatterClientPostingCurrencies(matterId);
                if (cancelled) return;
                const codes = [
                    ...new Set(
                        (rows || [])
                            .map((r) => r?.currencyCode)
                            .filter((c) => typeof c === "string" && c.trim()),
                    ),
                ];
                setClientPostingCurrencies(codes);
            } catch {
                if (!cancelled) setClientPostingCurrencies([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [matterId, multiCurrencyEnabled, fromCaseFile]);

    useEffect(() => {
        if (!fromCaseFile || !matterId) return;
        if (activeTab?.type === "Undertakings") return;
        loadPostingsForActiveTab();
    }, [
        fromCaseFile,
        matterId,
        showReversals,
        selectedTabId,
        activeTab?.type,
        loadPostingsForActiveTab,
    ]);

    useEffect(() => {
        if (!userSettings?.length) return;
        const savedShowDDA = userSettings?.find(
            (s) => s.key === USER_SETTING_SHOW_DDA_KEY,
        )?.value;
        const savedShowReversals = userSettings?.find(
            (s) => s.key === USER_SETTING_SHOW_REVERSALS_KEY,
        )?.value;

        if (savedShowDDA !== undefined) {
            setShowDDA(savedShowDDA === true || savedShowDDA === "true");
        }
        if (savedShowReversals !== undefined) {
            setShowReversals(
                savedShowReversals === true || savedShowReversals === "true",
            );
        }
    }, [userSettings]);

    useEffect(() => {
        if (refreshClicked && activeItem === 6) {
            if (fromCaseFile && activeTab?.type === "Undertakings") {
                setRefreshTrigger((prev) => !prev);
            } else {
                apiDataFetcher(reference);
            }
            setRefreshClicked(false);
        }
    }, [
        refreshClicked,
        activeItem,
        apiDataFetcher,
        reference,
        setRefreshClicked,
        fromCaseFile,
        activeTab?.type,
    ]);

    useEffect(() => {
        if (!fromCaseFile && reference) {
            apiDataFetcher(reference);
        }
    }, [reference, apiDataFetcher, fromCaseFile]);

    const handleToggleDDA = async () => {
        const nextValue = !showDDA;
        setShowDDA(nextValue);
        try {
            await updateCurrentUserSettings([
                { key: USER_SETTING_SHOW_DDA_KEY, value: nextValue },
            ]);
        } catch (error) {}
    };

    const handleToggleShowReversals = async () => {
        const nextValue = !showReversals;
        setShowReversals(nextValue);
        try {
            await updateCurrentUserSettings([
                { key: USER_SETTING_SHOW_REVERSALS_KEY, value: nextValue },
            ]);
        } catch (error) {}
    };

    const reloadData = () => {
        if (activeTab?.type === "Undertakings") {
            setRefreshTrigger((prev) => !prev);
        } else {
            loadPostingsForActiveTab();
        }
    };

    const isClosed = matterInfo?.matterId && matterInfo?.closed;
    const prevTransTitle = matterInfo?.matterRef
        ? `Matter Previous Transactions [${matterInfo.matterRef} - ${matterInfo.details ?? ""}]`
        : "Matter Previous Transactions";

    const handleActionRef = (e, transactionNo) => {
        setTransNo(transactionNo);
        setShowForm(true);
    };

    const renderAllGrid = () => (
        <MatterLedgerGrid
            showDDA={showDDA}
            setShowDDA={handleToggleDDA}
            showReversals={showReversals}
            setShowReversals={handleToggleShowReversals}
            matterId={matterId}
            comp="ledgerCard"
            gridData={list}
            loading={loading}
            setOpenReport={fromCaseFile ? setOpenReport : undefined}
            matterRef={matterInfo?.matterRef}
            isClosed={isClosed}
            dateClosed={matterInfo?.dateClosed}
            closedByUserRef={matterInfo?.closedByUserRef}
            handleActionRef={fromCaseFile ? handleActionRef : undefined}
            prevTransTitle={prevTransTitle}
            fillAvailableHeight={fromCaseFile}
        />
    );

    const contentWrapperClass =
        "h-100 flex-grow-1 d-flex flex-column overflow-hidden";
    const contentWrapperStyle = { minHeight: 0 };

    const renderTabContent = () => {
        if (!activeTab) return null;

        if (activeTab.type === "ALL") {
            return renderAllGrid();
        }

        if (activeTab.type === "Undertakings") {
            return (
                <UndertakingsList
                    matterId={matterId}
                    matterRef={matterInfo?.matterRef}
                    clientId={matterInfo?.clientId}
                    refreshData={refreshTrigger}
                    showInTabs={true}
                    setOpenReport={setOpenReport}
                    isClosed={isClosed}
                    dateClosed={matterInfo?.dateClosed}
                    closedByUserRef={matterInfo?.closedByUserRef}
                    fillAvailableHeight={fromCaseFile}
                />
            );
        }

        return (
            <LedgerPostingGrid
                loading={loading}
                gridData={list}
                ledgerType={activeTab.type}
                currencyCode={activeTab.currencyCode}
                matterId={matterId}
                matterRef={matterInfo?.matterRef}
                setOpenReport={setOpenReport}
                isClosed={isClosed}
                dateClosed={matterInfo?.dateClosed}
                closedByUserRef={matterInfo?.closedByUserRef}
                prevTransTitle={prevTransTitle}
                showPreviousTransactions={Boolean(activeTab.currencyCode)}
                exportTabLabel={activeTab.label}
                handleActionRef={handleActionRef}
                fillAvailableHeight={fromCaseFile}
            />
        );
    };

    const renderTabContentWrapped = () => (
        <div className={contentWrapperClass} style={contentWrapperStyle}>
            {renderTabContent()}
        </div>
    );

    if (!fromCaseFile) {
        return renderAllGrid();
    }

    return (
        <div className="h-100 d-flex flex-column overflow-hidden">
            <div className="d-flex align-items-end gap-3">
                <div className="flex-shrink-0">
                    <LedgerViewTabBar
                        tabItems={tabItems}
                        selectedTabId={selectedTabId}
                        onSelect={(tab) => setSelectedTabId(matterLedgerTabId(tab))}
                    />
                </div>
                <MatterBalancesCard matterId={matterId} className="flex-grow-1 mb-3" />
            </div>
            <div
                className="flex-grow-1 d-flex flex-column overflow-hidden"
                style={{ minHeight: 0 }}
            >
                {renderTabContentWrapped()}
            </div>

            {showForm && (
                <ViewPostingDetails
                    reloadData={reloadData}
                    transNo={transNo}
                    cancelEdit={() => setShowForm(false)}
                />
            )}

            {openReport && (
                <Dialog
                    title="Matter Ledger Report"
                    onClose={() => setOpenReport(false)}
                    className="dialog pdfwindow fs-md"
                    minWidth="60vw"
                >
                    <div className="px-3 pt-1" style={{ minHeight: "65vh" }}>
                        <ReportViewer
                            modalHeight="65vh"
                            name={getMatterLedgerReportName(activeTab)}
                            parameters={{
                                matterId,
                                reportDocumentName: `Matter Ledger - ${matterInfo?.matterRef ?? ""}`,
                                ...getMatterLedgerReportParams(activeTab),
                            }}
                            url={`${ReportURL}/Reporting/reports`}
                        />
                    </div>
                    <div className="dialog-footer">
                        <button
                            className="common-btn me-5"
                            type="button"
                            onClick={() => setOpenReport(false)}
                        >
                            Close
                        </button>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
